import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { TodoTasksBoard } from '@demind-inc/core';

import {
  getOAuthAccessToken,
  integrationsApi,
  tasksApi,
  useInitTodoIntegration,
} from '../../../data-access';
import './NotionAuthCallback.scss';
import { Path } from '../../../routing/path';
import { getTodoOAuthExchangeRedirectUrl, trackEventMixpanel } from '../../../utils';

interface ErrorState {
  message: string;
  fatal: boolean;
}

const NOTION_SEARCH_LIMIT = 100;

const NotionAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initTodoIntegration } = useInitTodoIntegration();

  const hasExchanged = useRef(false);
  const hasInitializedSelection = useRef(false);
  const hasAppliedExistingSelection = useRef(false);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [redirectDestination, setRedirectDestination] = useState<string>(Path.DASHBOARD);

  const [databases, setDatabases] = useState<Partial<TodoTasksBoard>[]>([]);
  const [selectedDatabaseIds, setSelectedDatabaseIds] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('');

  const [isExchangeInFlight, setIsExchangeInFlight] = useState(false);
  const [isFetchingDatabases, setIsFetchingDatabases] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, 350);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  useEffect(() => {
    const exchange = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code) {
        navigate(Path.DASHBOARD);
        return;
      }

      if (hasExchanged.current) return;
      hasExchanged.current = true;

      setIsExchangeInFlight(true);
      setErrorState(null);

      try {
        let redirectTo = Path.DASHBOARD;
        let stateUserId = '';
        let mobileFlag = false;

        if (state) {
          try {
            const stateUrl = new URL(state);
            const params = stateUrl.searchParams;

            redirectTo = params.get('redirectTo') || Path.DASHBOARD;
            stateUserId = params.get('userId') || '';
            mobileFlag = params.get('isMobileApp')?.includes('true') ?? false;
          } catch (error) {
            console.error('Failed to parse Notion state', error);
          }
        }

        // Fallback to query params in case the provider returns them directly
        const fallbackRedirectTo = searchParams.get('redirectTo');
        const fallbackUserId = searchParams.get('userId');
        const fallbackIsMobile = searchParams.get('isMobileApp')?.includes('true');

        redirectTo = fallbackRedirectTo || redirectTo;
        stateUserId = fallbackUserId || stateUserId;
        mobileFlag = fallbackIsMobile ?? mobileFlag;

        setRedirectDestination(redirectTo);
        setIsMobileApp(mobileFlag);
        setUserId(stateUserId);

        if (!stateUserId) {
          throw new Error('Missing user information.');
        }

        const { data } = await integrationsApi.exchangeCode(
          code,
          'notion',
          getTodoOAuthExchangeRedirectUrl(Path.CALLBACK.NOTION_CALLBACK, searchParams)
        );

        const token = (data as { accessToken?: string })?.accessToken;

        if (!token) {
          throw new Error('No access token returned.');
        }

        setAccessToken(token);
      } catch (error) {
        console.error('Error during Notion authentication:', error);
        setErrorState({ message: "Couldn't connect to Notion — try again?", fatal: true });
        hasExchanged.current = false;
      } finally {
        setIsExchangeInFlight(false);
      }
    };

    exchange();
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let isActive = true;
    setIsFetchingDatabases(true);

    integrationsApi
      .getOriginalTodoBoards(
        'notion',
        accessToken,
        debouncedSearchInput || undefined,
        NOTION_SEARCH_LIMIT
      )
      .then(({ data }) => {
        if (!isActive) return;
        const fetchedDatabases = (data as Partial<TodoTasksBoard>[]) || [];
        setDatabases(fetchedDatabases);
        if (!hasInitializedSelection.current) {
          setSelectedDatabaseIds(
            new Set(fetchedDatabases.filter((db) => !db.closed).map((db) => db.originalBoardId))
          );
          hasInitializedSelection.current = true;
        }

        // Fetch existing projects and pre-select databases that are already connected
        if (!userId || hasAppliedExistingSelection.current) {
          return;
        }
        if (!getOAuthAccessToken()) {
          return [fetchedDatabases, null] as const;
        }
        return tasksApi
          .getTodoProjects(userId)
          .then((response) => [fetchedDatabases, response] as const)
          .catch((error) => {
            console.warn('Failed to fetch existing Notion projects', error);
            return [fetchedDatabases, null] as const;
          });
      })
      .then((result) => {
        if (!isActive || !result) return;
        const [fetchedDatabases, response] = result;
        const projects = (response?.data as TodoTasksBoard[]) || [];
        const notionProjects = projects.filter(
          (project) => project.appFrom === 'notion' && !project.closed
        );
        const existingDatabaseIds = new Set(
          notionProjects.map((project) => project.originalBoardId).filter(Boolean) as string[]
        );

        // Select databases that match existing originalBoardIds
        const databasesToSelect = fetchedDatabases
          .map((db) => db.originalBoardId)
          .filter((id) => id && existingDatabaseIds.has(id)) as string[];

        if (databasesToSelect.length > 0) {
          setSelectedDatabaseIds(new Set(databasesToSelect));
        }
        hasAppliedExistingSelection.current = true;
      })
      .catch((error) => {
        console.error('Failed to fetch Notion databases', error);
        if (!isActive) return;
        setErrorState({ message: "Couldn't load your Notion databases.", fatal: true });
      })
      .finally(() => {
        if (!isActive) return;
        setIsFetchingDatabases(false);
      });

    return () => {
      isActive = false;
    };
  }, [accessToken, userId, debouncedSearchInput]);

  const visibleDatabaseIds = databases
    .map((db) => db.originalBoardId)
    .filter((id): id is string => Boolean(id));
  const selectedVisibleCount = visibleDatabaseIds.filter((id) =>
    selectedDatabaseIds.has(id)
  ).length;

  const handleToggleSelectAll = () => {
    setSelectedDatabaseIds((prev) => {
      if (!visibleDatabaseIds.length) {
        return prev;
      }
      const next = new Set(prev);

      if (selectedVisibleCount === visibleDatabaseIds.length) {
        visibleDatabaseIds.forEach((id) => next.delete(id));
      } else {
        visibleDatabaseIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const handleToggleDatabase = (databaseId: string) => {
    setSelectedDatabaseIds((prev) => {
      const next = new Set(prev);

      if (next.has(databaseId)) {
        next.delete(databaseId);
      } else {
        next.add(databaseId);
      }

      return next;
    });
  };

  const handleSync = async () => {
    if (!userId) {
      setErrorState({ message: "We're missing some account info — try connecting again.", fatal: true });
      return;
    }

    const databaseIds = Array.from(selectedDatabaseIds);

    if (!databaseIds.length) {
      return;
    }

    if (!accessToken) {
      setErrorState({ message: "We couldn't verify your login — try connecting again.", fatal: true });
      return;
    }

    try {
      setErrorState(null);
      setIsSyncing(true);
      await initTodoIntegration({
        token: accessToken,
        todoType: 'notion',
        userId: userId,
        shouldSyncTasks: true,
        skipRefresh: isMobileApp,
        originalBoardIds: databaseIds,
      });

      trackEventMixpanel('connect_todo_auth_done', {
        todoType: 'notion',
        fromMobileApp: isMobileApp,
        databasesSelected: databaseIds.length,
      });

      if (isMobileApp) {
        window.location.href = 'lifestack://todo-callback';
        return;
      }

      navigate(redirectDestination || Path.DASHBOARD);
    } catch (error) {
      console.error('Failed to start Notion sync', error);
      setErrorState({
        message: "Couldn't start syncing those databases.",
        fatal: false,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isExchangeInFlight && !accessToken && !errorState) {
    return (
      <div className="notion-auth-callback">
        <CircularProgress />
      </div>
    );
  }

  const allSelected =
    visibleDatabaseIds.length > 0 && selectedVisibleCount === visibleDatabaseIds.length;
  const isIndeterminate = selectedVisibleCount > 0 && !allSelected;
  const fatalError = errorState?.fatal;
  const errorMessage = errorState?.message;

  return (
    <div className="notion-auth-callback">
      <div className="notion-auth-callback__card">
        <Typography variant="h5" component="h1" className="notion-auth-callback__title">
          Select Notion databases
        </Typography>

        {fatalError ? (
          <div className="notion-auth-callback__error">
            <Typography color="error" variant="body2" className="notion-auth-callback__error-text">
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(redirectDestination || Path.DASHBOARD)}
            >
              Back to dashboard
            </Button>
          </div>
        ) : (
          <div className="notion-auth-callback__body">
            {errorMessage ? (
              <Typography
                color="error"
                variant="body2"
                className="notion-auth-callback__error-text"
              >
                {errorMessage}
              </Typography>
            ) : null}

            <Typography variant="body2" className="notion-auth-callback__description">
              Choose the databases you want Lifestack to import tasks from.
            </Typography>

            <TextField
              size="small"
              fullWidth
              placeholder="Search Notion databases..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="notion-auth-callback__search"
            />

            <Typography variant="body2" className="notion-auth-callback__selected-count">
              Selected {selectedDatabaseIds.size} database
              {selectedDatabaseIds.size === 1 ? '' : 's'}
            </Typography>

            {databases.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                  />
                }
                label="Select all databases"
                className="notion-auth-callback__select-all"
              />
            )}

            <div className="notion-auth-callback__list">
              {isFetchingDatabases ? (
                <div className="notion-auth-callback__loading">
                  <CircularProgress size={24} />
                </div>
              ) : databases.length ? (
                databases.map((db) => {
                  const id = db.originalBoardId;
                  if (!id) {
                    return null;
                  }
                  const name = db.name || 'Untitled database';
                  const description = (db as any)?.desc || (db as any)?.description;
                  const isChecked = selectedDatabaseIds.has(id);

                  return (
                    <label
                      key={id}
                      className="notion-auth-callback__database"
                      htmlFor={`database-${id}`}
                    >
                      <Checkbox
                        id={`database-${id}`}
                        checked={isChecked}
                        onChange={() => handleToggleDatabase(id)}
                      />
                      <div className="notion-auth-callback__database-info">
                        <span className="notion-auth-callback__database-name">{name}</span>
                        {description ? (
                          <span className="notion-auth-callback__database-description">
                            {description}
                          </span>
                        ) : null}
                      </div>
                    </label>
                  );
                })
              ) : (
                <Typography variant="body2" className="notion-auth-callback__empty">
                  No databases available for import.
                </Typography>
              )}
            </div>

            <div className="notion-auth-callback__actions">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSync}
                disabled={selectedDatabaseIds.size === 0 || isSyncing || !!fatalError}
                className="notion-auth-callback__sync-button"
              >
                {isSyncing ? (
                  <>
                    <CircularProgress size={18} className="notion-auth-callback__button-spinner" />
                    Syncing databases
                  </>
                ) : (
                  'Import selected databases'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotionAuthCallback;
